import isEmail from "validator/lib/isEmail";
import isURL from "validator/lib/isURL";

const hasPhoneNumber = (text: string): string | null => {
  const match = text.match(/(?:\d[\s\-.()+]*){6,}/);
  return match ? match[0] : null;
};

const hasLink = (text: string): string | null => {
  // Split text into words to check each potential URL
  const words = text.split(/[\s,]+/);

  // Check for valid URLs using validator
  const urlWord = words.find((word) => {
    try {
      // Remove common punctuation that might trail URLs
      const cleaned = word.replace(/[.,;:!?)]$/, "");
      return isURL(cleaned, {
        protocols: ["http", "https", "ftp"],
        require_protocol: false,
        require_valid_protocol: true,
        allow_underscores: true,
      });
    } catch {
      return false;
    }
  });

  if (urlWord) return urlWord;

  // Fallback regex patterns for common URL formats
  const urlPatterns = [
    // Standard URLs with protocol
    /https?:\/\/[^\s]+/,
    // URLs without protocol but with common TLDs
    /(?:www\.)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?/,
    // Domain.TLD patterns (more specific to avoid false positives)
    /\b[a-zA-Z0-9-]+\.(?:com|org|net|edu|gov|io|co|uk|de|fr|jp|cn|in|au|br|ru|us|info|biz|me|app|dev)\b(?:\/[^\s]*)?/,
  ];

  for (const pattern of urlPatterns) {
    const match = text.match(pattern);
    if (match) return match[0];
  }

  return null;
};

/**
 * Detects if a string contains any form of contact information
 * including phone numbers, emails, and social media references
 * @param {string} text - The input text to check
 * @returns {string | null} - The contact information found, or null if none detected
 */
export const hasContactInfo = (text: string | undefined): string | null => {
  if (!text || typeof text !== "string") return null;

  // Convert to lowercase for case-insensitive matching
  const normalizedText = text.toLowerCase();

  // Check for phone numbers
  const phoneMatch = hasPhoneNumber(normalizedText);
  if (phoneMatch) return phoneMatch;

  // Check for email using validator
  const words = normalizedText.split(/[\s,]+/);
  const emailWord = words.find((word) => {
    try {
      return isEmail(word);
    } catch {
      return false;
    }
  });
  if (emailWord) return emailWord;

  // Check for links (using original text to preserve case for URLs)
  const linkMatch = hasLink(text);
  if (linkMatch) return linkMatch;

  // Social media patterns - keeping our comprehensive checks
  const socialPatterns = [
    // Direct mentions
    /(?:^|\s)@[\w.-]+/, // Generic @ mention
    // Platform specific patterns with variations
    {
      platform: "instagram",
      patterns: [
        /(?:instagram\.com|insta\w*\b|(?:^|\s)ig\b|(?:^|\s)inst\b(?!ant|ead|inct|ill|all|ance|ead|ruct|rument)|ig:|inst:|gram:|инст:|иг:|инстаграм\b)[\s/]*[@]?[\w.-]+/,
        /(?:\b\w*(?<!pro)gram\w*\b|\binsta\w*\b)[\s:]+.*?[\w.-]+/,
      ],
    },
    {
      platform: "facebook",
      patterns: [
        /(?:facebook\.com|fb\b|fb:|facebook:)[\s/]?[@]?[\w.-]+/,
        /(?:\bfacebook\b|\bfb\b)[\s:].*?[\w.-]+/,
      ],
    },
    {
      platform: "linkedin",
      patterns: [
        /(?:linkedin\.com|linkedin:)[\s/]?[@]?[\w.-]+/,
        /(?:\blinkedin\b)[\s:].*?[\w.-]+/,
      ],
    },
    {
      platform: "twitter",
      patterns: [
        /(?:twitter\.com|x\.com|\bx\b|tweet:|twitter:)[\s/]?[@]?[\w.-]+/,
        /(?:\btwitter\b|\btweet\b)[\s:].*?[\w.-]+/,
      ],
    },
    {
      platform: "whatsapp",
      patterns: [
        /(?:whatsapp\.com|wa\b|wa:|whatsapp:)[\s/]?[@]?[\w.-]+/,
        /(?:\bwhatsapp\b|\bwa\b)[\s:].*?[\w.-]+/,
      ],
    },
    {
      platform: "telegram",
      patterns: [
        /(?:telegram\.com|t\.me|tg\b|tg:|telegram:|тг:|тг|тг-\b)[\s/]?[@]?[\w.-]+/,
        /(?:\btelegram\b|\btg\b)[\s:].*?[\w.-]+/,
      ],
    },
    {
      platform: "vk",
      patterns: [
        /(?:vk\.com|vk\b|vk:\b)[\s/]?[@]?[\w.-]+/,
        /(?:\bvk\b)[\s:].*?[\w.-]+/,
      ],
    },
  ];

  // Common contact-related keywords
  const contactKeywords = [
    /\b(?:dm|message|msg|contact|follow|ping)\b/,
    /\b(?:whatsapp|telegram|signal|viber|line|wechat)\b/,
    /\b(?:call|text|sms|phone|email|mail)\b/,
  ];

  // Check all social patterns
  for (const platform of socialPatterns) {
    if (platform instanceof RegExp) {
      const match = normalizedText.match(platform);
      if (match) return match[0];
    } else {
      for (const pattern of platform.patterns) {
        const match = normalizedText.match(pattern);
        if (match) return match[0];
      }
    }
  }

  // Check contact keywords
  for (const pattern of contactKeywords) {
    const match = normalizedText.match(pattern);
    if (match) return match[0];
  }

  return null;
};
